import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { EmployeeDashboard } from "../modules/dashboard/employee-dashboard"
import { ManagerDashboard } from "../modules/dashboard/manager-dashboard"
import { HRDashboard } from "../modules/dashboard/hr-dashboard"
import { Button } from "../ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog"
import { Plus, Calendar, LayoutDashboard } from "lucide-react"
import { AddLeaveForm } from "../modules/add-view leaves/add-leave-form"
import NoRoleAssignedPage from "./no-role-assigned/page"

interface DashboardProps {
  sidebarOpen?: boolean
}

export default function Dashboard({sidebarOpen }: DashboardProps) {
  const [activeView, setActiveView] = useState("employee")
  const [roleDashboard, setRoleDashboard] = useState<string[]>([])
  const [userName, setUserName] = useState("User")
  const [hasRoles, setHasRoles] = useState(true)
  const [isAddLeaveModalOpen, setIsAddLeaveModalOpen] = useState(false)
  const navigate = useNavigate()

  // Map role IDs to names (excluding admin)
  const roleMap: { [key: number]: string } = {
    1: "hr",
    2: "manager",
    3: "employee",
  };

  const handleAddTimeClick = () => {
    navigate("/timesheet")
  }

  const handleAddLeaveClick = () => {
    setIsAddLeaveModalOpen(true)
  }

  useEffect(() => {
    // Read roles from localStorage
    const storedRoles = JSON.parse(localStorage.getItem("roles") || "[]") as number[];
    
    const dashboardRoles = storedRoles.filter(
  r => r === 1 || r === 2 || r === 3
);

const hasDashboardRole = dashboardRoles.length > 0;

if (!hasDashboardRole && storedRoles.length > 0) {
  // pick default page (admin, co-owner, etc.)
  if (storedRoles.includes(4)) {
    navigate("/config-settings", { replace: true });
    return;
  }

  if (storedRoles.includes(7)) {
    navigate("/manage-engagements", { replace: true });
    return;
  }

  if (storedRoles.includes(6)) {
    navigate("/pending-approvals/leave", { replace: true });
    return;
  }
}

    
    // Map to role names and filter out admin
    const mappedRoles = dashboardRoles
      .map((r) => roleMap[r])
      .filter(Boolean)
      .filter((role) => role !== "admin"); // Extra filter to ensure admin is not included

    // Check if user has any valid dashboard roles
    if (mappedRoles.length === 0) {
      setHasRoles(true)
      return
    }

    setHasRoles(true)
    
    // Default to "employee" if available, otherwise first available role
    const defaultRole = mappedRoles.includes("employee") ? "employee" : mappedRoles[0]
    setRoleDashboard(mappedRoles)
    setActiveView(defaultRole)

    // Get the actual logged-in user's name
    const storedUserName = localStorage.getItem("userName") || sessionStorage.getItem("userName");
    if (storedUserName) {
      setUserName(storedUserName);
    }
  }, []);

  const getRoleBasedDashboard = () => {
    switch (activeView) {
      case "employee":
        return <EmployeeDashboard userRoles={roleDashboard} />;
      case "manager":
        return <ManagerDashboard userRoles={roleDashboard} />;
      case "hr":
        return <HRDashboard userRoles={roleDashboard} />;
      default:
        return <EmployeeDashboard userRoles={roleDashboard} />;
    }
  };

  // If user has no roles, show the no-role assigned page
  if (!hasRoles) {
    return <NoRoleAssignedPage />
  }

  return (
    <div
      className={`w-full mb-6 p-4 lg:p-6 overflow-x-hidden ${!sidebarOpen ? "max-w-none" : "max-w-7xl mx-auto"}`}
    >

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4 mb-2">

        {/* Title and Welcome Text */}
        <div>

          {/* Row with Icon + Dashboard */}
          <div className="flex items-center space-x-2">
            <LayoutDashboard className="h-6 w-6 text-blue-600" />
            <h1
              className={`text-3xl font-bold text-gray-900`}
            >
              Dashboard
            </h1>
          </div>

          {/* Welcome message below */}
          <p className={"text-gray-600"}>
            Welcome back, {userName}
          </p>
        </div>


        {/* Role Buttons */}
        <div className="flex justify-center items-center gap-1 flex-1">
          <div className="mx-auto flex gap-2">
            {roleDashboard.map((roleName) => (
              <button
                key={roleName}
                onClick={() => setActiveView(roleName)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors capitalize ${activeView === roleName
                    ? "bg-blue-600 text-white": "text-gray-700 hover:bg-gray-200" }`}
              >
                {roleName === "hr" ? "HR" : roleName}
              </button>
            ))}
          </div>
        </div>


        {/* Action Buttons */}
        <div className="flex gap-2">
          <Button
            variant="outline"
            className="h-10 flex flex-row items-center gap-2 bg-white px-4 py-2"
            onClick={handleAddTimeClick}
          >
            <Plus className="w-4 h-4" />
            <span className="text-sm">Add Time</span>
          </Button>

          <Button
            variant="outline"
            className="h-10 flex flex-row items-center gap-2 bg-white px-4 py-2"
            onClick={handleAddLeaveClick}
          >
            <Calendar className="w-4 h-4" />
            <span className="text-sm">Add Leave</span>
          </Button>
        </div>
      </div>

      {/* Role-based Dashboard */}
      {getRoleBasedDashboard()}

      {/* Add Leave Modal */}
      <Dialog open={isAddLeaveModalOpen} onOpenChange={setIsAddLeaveModalOpen}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New Leave Request</DialogTitle>
          </DialogHeader>
          <AddLeaveForm
            onSubmitSuccess={() => {
              setIsAddLeaveModalOpen(false)
            }}
          />
        </DialogContent>
      </Dialog>
    </div>
  )
}